/**
 * «Где моя машина»: запомнить место, вернуться к нему, забыть.
 * Вынос из actions.ts по лимиту 500 строк (машина состояний осталась там,
 * маршрут к машине идёт обычным selectTarget → showRoute — то есть получает
 * и AR-ведение, и все режимы бесплатно).
 */
import { haversineM, type LonLat } from '@app/geo'
import { getLang } from '@app/i18n'
import { isParking, localized } from '@app/rvf'
import { selectTarget, showRoute } from './actions'
import { clearCar, type ParkedCar, reflectCarInUrl, saveCar } from './car'
import { anchorId, parkedCar, venue } from './state'

/** Парковочная зона, ближайшая к точке (для «запомнить машину» по QR-якорю). */
function nearestParkingUnit(point: LonLat, levelId: string): { id: string; label: string } | null {
  const v = venue.value
  if (!v) return null
  let best: { id: string; label: string; dist: number } | null = null
  for (const unit of v.units) {
    if (unit.level !== levelId || !isParking(v, unit.category)) continue
    const ring = unit.geometry.coordinates[0] ?? []
    const c = ring[0]
    if (!c) continue
    const dist = haversineM(point, [c[0] ?? 0, c[1] ?? 0])
    const label = localized(unit.name, getLang(), v.venue.default_language) || unit.id
    if (!best || dist < best.dist) best = { id: unit.id, label, dist }
  }
  return best ? { id: best.id, label: best.label } : null
}

/** Запомнить место машины: явной зоной или по текущему QR-якорю на парковке. */
export function rememberCar(unitId?: string): boolean {
  const v = venue.value
  if (!v) return false
  let target: { id: string; label: string; levelId: string } | null = null
  if (unitId) {
    const unit = v.units.find((u) => u.id === unitId && isParking(v, u.category))
    if (unit)
      target = {
        id: unit.id,
        label: localized(unit.name, getLang(), v.venue.default_language) || unit.id,
        levelId: unit.level,
      }
  } else {
    const anchor = v.anchors.find((a) => a.id === anchorId.value)
    const near = anchor ? nearestParkingUnit(anchor.point, anchor.level) : null
    if (anchor && near) target = { ...near, levelId: anchor.level }
  }
  if (!target) return false
  const car: ParkedCar = {
    venueId: v.venue.id,
    targetId: target.id,
    levelId: target.levelId,
    label: target.label,
    savedAt: nowIso(),
  }
  saveCar(car)
  parkedCar.value = car
  reflectCarInUrl(target.id)
  return true
}

/** Построить маршрут обратно к сохранённой машине. */
export function returnToCar(): void {
  const car = parkedCar.value
  if (!car) return
  selectTarget(car.targetId)
  showRoute()
}

export function forgetCar(): void {
  const v = venue.value
  if (v) clearCar(v.venue.id)
  parkedCar.value = null
  reflectCarInUrl(null)
}

function nowIso(): string {
  try {
    return new Date().toISOString()
  } catch {
    return ''
  }
}
